// Biometric Authentication Utility using WebAuthn API
// For fingerprint authentication on mobile devices

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Check if WebAuthn is supported
export const isBiometricSupported = () => {
  return window.PublicKeyCredential !== undefined && 
         navigator.credentials !== undefined;
};

// Check if device is mobile OR a tablet with touch (covers iPad on iPadOS 13+
// which advertises a Mac user-agent by default, and Android tablets)
export const isMobileDevice = () => {
  const ua = navigator.userAgent || '';
  // Classic mobile/tablet UAs
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }
  // iPadOS 13+ reports as Macintosh; detect by touch capability
  const isIPadOnMacUA = /Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  if (isIPadOnMacUA) {
    return true;
  }
  // Fallback: small viewport or touch-primary device
  if (window.innerWidth <= 768) {
    return true;
  }
  return false;
};

// Check if biometric is available (supported + mobile/tablet).
// Also queries the platform authenticator when possible so devices with
// Touch ID / Face ID / Windows Hello on desktop can enroll too.
export const isBiometricAvailable = () => {
  return isBiometricSupported() && isMobileDevice();
};

// Async check: does the current device actually have a platform authenticator
// available (Touch ID, Face ID, Windows Hello, Android biometric)? Use this
// alongside isBiometricAvailable when you want the most accurate answer.
export const isPlatformAuthenticatorAvailable = async () => {
  try {
    if (!isBiometricSupported()) return false;
    if (typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') {
      return false;
    }
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (e) {
    return false;
  }
};

// Helper to convert string to Uint8Array
const str2ab = (str) => {
  const buf = new ArrayBuffer(str.length);
  const bufView = new Uint8Array(buf);
  for (let i = 0; i < str.length; i++) {
    bufView[i] = str.charCodeAt(i);
  }
  return bufView;
};

// Helper to convert ArrayBuffer to base64
const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
};

// Helper to convert base64 to ArrayBuffer
const base64ToArrayBuffer = (base64) => {
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

// Register biometric credential
export const registerBiometric = async (userEmail, authToken) => {
  try {
    // Generate a random challenge
    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);
    
    // Create credential options
    const publicKeyCredentialCreationOptions = {
      challenge: challenge,
      rp: {
        name: "Dine-A-Saurus",
        id: window.location.hostname
      },
      user: {
        id: str2ab(userEmail),
        name: userEmail,
        displayName: userEmail
      },
      pubKeyCredParams: [
        { type: "public-key", alg: -7 }, // ES256
        { type: "public-key", alg: -257 } // RS256
      ],
      authenticatorSelection: {
        // Force the device's built-in biometric sensor (Touch ID / Face ID /
        // Samsung fingerprint / Windows Hello). "discouraged" + explicit
        // requireResidentKey:false tells the OS NOT to route this through a
        // cloud-synced passkey picker (Samsung Passkey, iCloud Keychain,
        // Google Password Manager) — which was hijacking Samsung Android
        // registration and prompting a passkey instead of the fingerprint.
        authenticatorAttachment: "platform",
        userVerification: "required",
        requireResidentKey: false,
        residentKey: "discouraged"
      },
      timeout: 60000,
      attestation: "none"
    };

    // Create credential
    const credential = await navigator.credentials.create({
      publicKey: publicKeyCredentialCreationOptions
    });

    if (!credential) {
      throw new Error("Failed to create credential");
    }

    // Extract credential data
    const credentialId = arrayBufferToBase64(credential.rawId);
    const publicKey = arrayBufferToBase64(credential.response.getPublicKey());

    // Register with backend
    const response = await fetch(`${API_URL}/api/auth/register-biometric`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        credential_id: credentialId,
        public_key: publicKey
      })
    });

    if (!response.ok) {
      throw new Error('Failed to register biometric');
    }

    // Store credential ID locally as a JSON array so the same browser can
    // eventually hold multiple credentials (rare but possible) and remain
    // compatible with syncBiometricEnrollment which reads a list.
    localStorage.setItem(`biometric_${userEmail}`, JSON.stringify([credentialId]));

    return { success: true, credentialId };
  } catch (error) {
    console.error('Biometric registration error:', error);
    return { success: false, error: error.message };
  }
};

// Read the list of credential IDs saved for this email in this browser.
// Backward-compatible: pre-v7.9 saved a plain string; treat it as a 1-item list.
const readStoredCredentialIds = (userEmail) => {
  const raw = localStorage.getItem(`biometric_${userEmail}`);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    if (typeof parsed === 'string' && parsed) return [parsed];
    return [];
  } catch (e) {
    // Legacy: raw was a plain (non-JSON) string
    return [raw];
  }
};

// Verify biometric (login)
export const verifyBiometric = async (userEmail) => {
  try {
    // Get stored credential IDs (may be multiple — one per device registered
    // for this account). Backward-compatible with the single-string form.
    const storedIds = readStoredCredentialIds(userEmail);

    if (storedIds.length === 0) {
      throw new Error('No biometric credential found');
    }

    // Generate challenge
    const challenge = new Uint8Array(32);
    window.crypto.getRandomValues(challenge);

    // Build allowCredentials from EVERY registered device — the browser will
    // pick the one whose private key exists on this specific device.
    // transports:["internal"] is critical on iPad Safari — without it, Safari
    // shows a QR / "use another device" sheet instead of prompting the local
    // Face ID / Touch ID directly.
    const publicKeyCredentialRequestOptions = {
      challenge: challenge,
      allowCredentials: storedIds.map((id) => ({
        type: 'public-key',
        id: base64ToArrayBuffer(id),
        transports: ['internal'],
      })),
      userVerification: 'required',
      timeout: 60000
    };

    // Get credential (triggers biometric prompt)
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyCredentialRequestOptions
    });

    if (!assertion) {
      throw new Error("Biometric verification failed");
    }

    // Extract assertion data
    const authenticatorData = arrayBufferToBase64(assertion.response.authenticatorData);
    const clientDataJSON = arrayBufferToBase64(assertion.response.clientDataJSON);
    const signature = arrayBufferToBase64(assertion.response.signature);

    // The credential_id the user actually authenticated with — send that one
    // to the backend so it can match against the enrolled device.
    const usedCredentialId = arrayBufferToBase64(assertion.rawId);

    // Verify with backend
    const response = await fetch(`${API_URL}/api/auth/verify-biometric`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: userEmail,
        credential_id: usedCredentialId,
        authenticator_data: authenticatorData,
        client_data_json: clientDataJSON,
        signature: signature
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Biometric verification failed');
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('Biometric verification error:', error);
    return { success: false, error: error.message };
  }
};

// Check if user has biometric enrolled
export const hasBiometricEnrolled = (userEmail) => {
  return localStorage.getItem(`biometric_${userEmail}`) !== null;
};

// Sync biometric enrollment state from the backend into localStorage.
// Needed because iOS Safari's Intelligent Tracking Prevention can clear
// localStorage after 7 days of inactivity, and Home-Screen PWAs use a
// separate localStorage bucket from Safari itself — so a user who enrolled
// once may otherwise appear "un-enrolled" locally even though the server
// still holds their credential.
export const syncBiometricEnrollment = async (userEmail) => {
  if (!userEmail) return { enrolled: false, credentialIds: [] };
  try {
    const response = await fetch(`${API_URL}/api/auth/biometric-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: userEmail }),
    });
    if (!response.ok) return { enrolled: false, credentialIds: [] };
    const data = await response.json();
    const key = `biometric_${userEmail}`;
    // Prefer the new plural field; fall back to legacy singular.
    const ids = Array.isArray(data.credential_ids) && data.credential_ids.length
      ? data.credential_ids
      : (data.credential_id ? [data.credential_id] : []);
    if (ids.length > 0) {
      const asJson = JSON.stringify(ids);
      if (localStorage.getItem(key) !== asJson) {
        localStorage.setItem(key, asJson);
      }
      return { enrolled: true, credentialIds: ids, credentialId: ids[0] };
    }
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
    return { enrolled: false, credentialIds: [] };
  } catch (e) {
    console.error('syncBiometricEnrollment error:', e);
    return { enrolled: false, credentialIds: [] };
  }
};

// Remove biometric credential
export const removeBiometric = async (userEmail, authToken) => {
  try {
    // Remove from backend
    const response = await fetch(`${API_URL}/api/auth/disable-biometric`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to disable biometric');
    }

    // Remove from localStorage
    localStorage.removeItem(`biometric_${userEmail}`);

    return { success: true };
  } catch (error) {
    console.error('Remove biometric error:', error);
    return { success: false, error: error.message };
  }
};

// Clear biometric data from localStorage (for PIN reset scenarios)
export const clearBiometricData = (userEmail) => {
  try {
    localStorage.removeItem(`biometric_${userEmail}`);
    localStorage.removeItem('remember_token');
    localStorage.removeItem('userEmail');
    console.log('Cleared biometric data for user:', userEmail);
    return true;
  } catch (error) {
    console.error('Error clearing biometric data:', error);
    return false;
  }
};
